/**
 * REST client for the per-room "Advanced Options" pane on the conference
 * edit page. Mirrors `backend/src/conferences/router.ts` GET / PATCH for
 * the `/conference-rooms/:id/options` resource shipped in Phase 12.1.
 *
 * The backend's `optionsService` returns `DEFAULT_OPTIONS` when no row
 * exists yet — the GET is therefore safe to call for any room id and
 * always resolves to a complete `ConferenceOptions` shape. PATCH is a
 * sparse upsert: only the keys present in the body are written, the rest
 * keep their stored / default value.
 */
import { apiFetch } from './client';

export type RecordingMode = 'off' | 'auto_on_mod_join' | 'manual';

export interface ConferenceOptions {
  mute_on_entry: boolean;
  wait_for_moderator: boolean;
  entry_chime: boolean;
  lock_on_full: boolean;
  /** 2..500 or null = "follow the room's max_participants". */
  participant_limit_override: number | null;
  recording_mode: RecordingMode;
  qa_mode_default: boolean;
  moderator_dtmf_enabled: boolean;
  caller_dtmf_enabled: boolean;
  /** 1–2 char DTMF key (e.g. '#', '*9'). */
  promotion_key: string;
  dial_out_enabled: boolean;
  qa_play_prerecorded: boolean;
  /** Optional FK to a recording the engine plays as wait music while
   *  listeners are waiting for a moderator. NULL = use the FreeSWITCH
   *  profile's stock MoH. Added in migration 0097. */
  wait_music_recording_id: string | null;
}

export type PatchConferenceOptionsBody = Partial<ConferenceOptions>;

export const conferenceOptions = {
  get: (roomId: string) =>
    apiFetch<ConferenceOptions>(`/api/conference-rooms/${roomId}/options`),
  patch: (roomId: string, body: PatchConferenceOptionsBody) =>
    apiFetch<ConferenceOptions>(
      `/api/conference-rooms/${roomId}/options`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
};
